import { CONDITIONS, CONDITION_NOTE, type ConditionContent } from "./conditions";
import { areaLabel, type AreaSummary, type LocalListing } from "./local-pages";
import { isConditionVerified } from "./copy-review";
import { specialtyContent } from "./specialty-content";
import { canonicalUrl, isThinSpecialty, type SeoMetadata } from "./structured-data";

/**
 * Titles, descriptions and JSON-LD for area, specialty×area and condition
 * pages. Shared by the SPA <SEO> and scripts/prerender.mjs so the static
 * head and the hydrated head never disagree.
 */
export interface LocalSeoMetadata extends SeoMetadata {
  noindex: boolean;
}

function fitTitle(base: string): string {
  const branded = `${base} | PulseBoard`;
  return branded.length <= 60 ? branded : base;
}

function fitDescription(value: string, maxLength = 160): string {
  if (value.length <= maxLength) return value;
  const shortened = value.slice(0, maxLength - 1);
  return `${shortened.slice(0, shortened.lastIndexOf(" ")).trimEnd()}…`;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function areaPath(areaSlug: string): string {
  return `/directory/area/${areaSlug}`;
}

export function specialtyAreaPath(specialtySlug: string, areaSlug: string): string {
  return `/directory/${specialtySlug}/${areaSlug}`;
}

export function conditionPath(slug: string): string {
  return `/conditions/${slug}`;
}

export function areaSeoMetadata(area: AreaSummary): LocalSeoMetadata {
  const path = areaPath(area.slug);
  const topSpecialties = area.specialties.slice(0, 3).map((item) => item.name.toLowerCase());
  return {
    title: fitTitle(`Specialists in ${area.name}, Canberra — ${area.count} listed`),
    description: fitDescription(
      `${area.count} specialists with rooms in ${areaLabel(area)}, Canberra${topSpecialties.length ? `, including ${topSpecialties.join(", ")}` : ""}. Practice addresses and contact details.`,
    ),
    path,
    canonical: canonicalUrl(path),
    noindex: isThinSpecialty(area.count),
  };
}

export function specialtyAreaHeading(specialtySlug: string, specialtyName: string, area: AreaSummary): string {
  const plural = specialtyContent(specialtySlug)?.practitionerPlural ?? `${specialtyName} specialists`;
  return `${capitalise(plural)} in ${area.name}`;
}

export function specialtyAreaSeoMetadata(
  specialty: { slug: string; name: string; count: number },
  area: AreaSummary,
): LocalSeoMetadata {
  const path = specialtyAreaPath(specialty.slug, area.slug);
  const singular = specialtyContent(specialty.slug)?.practitioner ?? `${specialty.name.toLowerCase()} specialist`;
  return {
    title: fitTitle(`${specialtyAreaHeading(specialty.slug, specialty.name, area)}, Canberra — ${specialty.count} listed`),
    description: fitDescription(
      `Find ${/^[aeiou]/i.test(singular) || /^ENT\b/.test(singular) ? "an" : "a"} ${singular} in ${areaLabel(area)}, Canberra: ${specialty.count} listed with practice addresses and contact details, plus how referrals and costs work.`,
    ),
    path,
    canonical: canonicalUrl(path),
    noindex: isThinSpecialty(specialty.count),
  };
}

export function conditionSeoMetadata(condition: ConditionContent, listingCount: number): LocalSeoMetadata {
  const path = conditionPath(condition.slug);
  return {
    title: fitTitle(condition.heading),
    description: fitDescription(
      `Who treats ${condition.name} in Canberra, how GP referrals work, and ${listingCount > 0 ? `${listingCount} listed specialists` : "specialists"} with practice details.`,
    ),
    path,
    canonical: canonicalUrl(path),
    // A condition page with no doctor behind it is a doorway, and unverified
    // copy is not ready to be found: keep both out of the index.
    noindex: listingCount === 0 || !isConditionVerified(condition),
  };
}

/**
 * Specialties that share a condition guide with this one (e.g. orthopaedics
 * ↔ rheumatology for joint pain), most shared first. Specialty pages had no
 * sideways links (audit report 14). Thin specialties are skipped.
 */
export function relatedSpecialties<T extends { slug: string; name: string; count: number }>(
  slug: string,
  specialties: T[],
  limit = 4,
): T[] {
  const shared = new Map<string, number>();
  for (const condition of CONDITIONS) {
    if (!condition.specialties.includes(slug)) continue;
    for (const other of condition.specialties) {
      if (other !== slug) shared.set(other, (shared.get(other) ?? 0) + 1);
    }
  }
  return specialties
    .filter((item) => shared.has(item.slug) && !isThinSpecialty(item.count))
    .sort((a, b) => (shared.get(b.slug) ?? 0) - (shared.get(a.slug) ?? 0) || a.name.localeCompare(b.name))
    .slice(0, limit);
}

export function breadcrumbStructuredData(items: Array<{ name: string; path: string }>): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

export function listingCollectionStructuredData(
  metadata: SeoMetadata,
  name: string,
  listings: LocalListing[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name,
    description: metadata.description,
    url: metadata.canonical,
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: listings.length,
      itemListElement: listings.map((listing, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: listing.name,
        url: canonicalUrl(`/directory/specialist/${listing.slug}`),
      })),
    },
  };
}

export function conditionStructuredData(
  condition: ConditionContent,
  metadata: SeoMetadata,
  specialtyNames: string[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    name: condition.heading,
    description: metadata.description,
    url: metadata.canonical,
    audience: { "@type": "PeopleAudience", audienceType: "Patient" },
    about: { "@type": "MedicalCondition", name: capitalise(condition.name) },
    specialty: specialtyNames,
    abstract: `${condition.summary} ${CONDITION_NOTE}`,
    inLanguage: "en-AU",
  };
}
